import { PACKAGING_TYPES } from 'constants/index';
import _isEmpty from 'lodash/isEmpty';
import { isUSA } from 'utils/paymentMethodUtils';
import { toTitleCase } from 'utils/commonUtils';
import { getSelectedValue, phoneToNumber, phoneNumberFormatter } from './commonUtils';
import { convertToShortType } from './cardTypeUtils';

const isGiftWrappable = (products) => products.every((product) => product.isGiftWrappable);

export const transformProductsForOrderReview = (products = []) => {
  const isOrderWrappable = isGiftWrappable(products);

  return products.map((product) => ({
    offerItemId: product.offerItemId,
    cmosSku: product.cmosskuId,
    quantity: product.quantity,
    price: product.price.adjustedPrice || product.price.currentPrice,
    isGiftWrap: isOrderWrappable && product.giftOptions.giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING,
  }));
};

/*
  Get Data for Order Review Request.
 */
export const mapDataForOrderReview = (clientsModule, farfetchCheck) => {
  const { conciergeProducts, farfetchConciergeProducts } = clientsModule.cart;
  const { shippingAddress } = clientsModule.clientDetails.data || {};
  const shippingAddressUS = shippingAddress?.filter(({ country }) => isUSA(country));
  const zipCode = getSelectedValue(shippingAddressUS).zip;
  const { keys, lastApplied } = clientsModule.checkout.promo;
  const promo = [...keys, ...lastApplied];

  const items = transformProductsForOrderReview(farfetchCheck ? farfetchConciergeProducts : conciergeProducts);

  return {
    promo,
    zipCode,
    items,
  };
};

const parseToNumber = (val) => parseFloat(val?.toString().replace('$', '') || 0);

const mapBopsAddress = (bopsAddress) => ({
  line1: bopsAddress.line1,
  line2: bopsAddress.line2,
  zip: bopsAddress.zip,
  country: bopsAddress.country || 'US',
  city: bopsAddress.city,
  state: bopsAddress.state,
  contactAddressName: bopsAddress.firstName,
});

/** Calculate amount for payments */
export const getAmountReceived = (orderTotalPrice, giftCardPayments = []) => {
  const orderTotalAmt = parseToNumber(orderTotalPrice);
  const gcAmountReceived = giftCardPayments.reduce((acc, obj) => acc + parseToNumber(obj.giftCardBalance), 0);
  const amountReceived = orderTotalAmt - gcAmountReceived;
  return amountReceived < 0 ? '0.00' : amountReceived.toFixed(2);
};

/** Calculate amount for gift cards */
export const getGCPayments = (orderTotalPrice, giftCardPayments = []) => {
  let total = parseToNumber(orderTotalPrice);
  return giftCardPayments.map(({ cardType, giftCardNumber, giftCardPin, giftCardBalance }) => {
    const gcBalance = parseToNumber(giftCardBalance);
    const gcAmount = total - gcBalance >= 0 ? gcBalance : total;
    total = total - gcBalance > 0 ? total - gcBalance : 0;
    return {
      cardType,
      giftCardNumber,
      giftCardPin,
      amountReceived: gcAmount.toFixed(2),
    };
  });
};

const mapItemsForOrderSubmit = (products = [], bopsAddress, shipmentMethod) => {
  const isOrderWrappable = isGiftWrappable(products);
  const isBopsOrder = !_isEmpty(bopsAddress);

  return products.map((product, index) => {
    const isPriceAdjusted = !!product.price?.adjustedPrice;
    const inStock = !!(product.bopsAvailability && product.bopsAvailability[Number(bopsAddress.storeNum || 0)]);
    const mappedItem = {
      offerItemId: product.offerItemId,
      cmosSku: product.cmosskuId,
      pimSku: product.itemskuId,
      quantity: product.quantity,
      shipmentOption: shipmentMethod.id,
      currencyCode: product.price.currencyCode,
      itemPriceEach: product.price.itemPriceExt,
      itemTaxEach: shipmentMethod.itemTaxes[index] || product.price.itemTax,
      freightEach: shipmentMethod.lineDeliveryCharges[index],
      otherEach: shipmentMethod.giftWrappingCharges[index] || '0.00',
      adjustedPriceEach: product.price.adjustedPrice || product.price.currentPrice,
      isIce: product.isIce,
      productName: product.name,
      productImageUrl: product.imageUrl || '',
      designerName: product.brand,
      color: product.color,
      size: product.size,
      departmentCode: product.departmentCode,
      ...(isOrderWrappable && {
        isGiftWrap: product.giftOptions?.giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING,
        giftmessage1: product.giftOptions?.giftMessage1,
        giftmessage2: product.giftOptions?.giftMessage2,
        giftmessage3: product.giftOptions?.giftMessage3,
        giftmessage4: product.giftOptions?.giftMessage4,
        giftmessage5: product.giftOptions?.giftMessage5,
      }),
      promotionCodes: product.promotionsApplied,
      promotionDiscAmt: product.promotionDiscAmt,
      ...(isPriceAdjusted && {
        priceOverrideReason: product.price?.priceOverrideReason,
        priceOverrideReasonDetails: product.price?.priceOverrideReasonDetails,
      }),
      createdBy: product.createdBy,
      ...(isBopsOrder && {
        storePickupFlag: 'true',
        ...(inStock ? { pickupStoreNumber: bopsAddress.storeNum } : { shiptoStoreNumber: bopsAddress.storeNum }),
      }),
      ...(product.arriveByDate && { arriveByDate: product.arriveByDate }),
    };

    return mappedItem;
  });
};

/*
  Building a giftReceiptData array from products list
*/
export const mapGiftReceiptDataForSubmit = (products = []) => {
  const itemsWithGiftReceipt = products.flatMap((product) =>
    product.giftOptions?.giftReceipt || product.giftReceipt
      ? { pimSku: product.itemskuId || product.pimSku, quantity: product.quantity }
      : [],
  );

  return itemsWithGiftReceipt.length === 0
    ? {}
    : {
        giftReceiptData: {
          items: itemsWithGiftReceipt,
        },
      };
};

/*
  Get Data for Order Submit Request.
 */
export const mapDataForOrderSubmit = (
  {
    clientData,
    conciergeProducts,
    shipmentMethod,
    promo,
    gcPayments,
    deliveryNumber,
    bopsAddress = {},
    isParentheticalOrder = false,
    alternateAssocPin,
    placeMaoOrder,
  },
  productsMapper = mapItemsForOrderSubmit,
) => {
  const { shippingAddress, payments } = clientData;
  const selectedAddress = Array.isArray(shippingAddress) ? getSelectedValue(shippingAddress) : shippingAddress;
  const payment = getSelectedValue(payments);
  const isBopsOrder = !_isEmpty(bopsAddress);
  const formattedParentheticalPhone = phoneNumberFormatter(deliveryNumber, 'dashes');
  const giftReceiptData = mapGiftReceiptDataForSubmit(conciergeProducts);
  const shippingPhones = [
    {
      type: 'DayPhone',
      number: phoneToNumber(selectedAddress.phone),
    },
  ];

  if (deliveryNumber) {
    shippingPhones.push({
      type: 'DeliveryPhone',
      number: phoneToNumber(deliveryNumber),
    });
  }

  const clientFirstName = clientData.firstName || payment.billingAddress.firstName;
  const clientLastName = clientData.lastName || payment.billingAddress.lastName;

  return {
    orderTotalAmt: shipmentMethod.orderTotalAmt.replace('$', ''),
    orderTotalTax: shipmentMethod.orderTotalTax.replace('$', ''),
    deliveryCharges: shipmentMethod.deliveryCharges.replace('$', ''),
    promotionsApplied: promo.codes,
    soldTo: {
      firstName: clientFirstName,
      lastName: clientLastName,
      email: clientData.email,
      profileId: clientData.clientId,
      address: payment.billingAddress || selectedAddress,
      phone: [
        {
          type: 'DayPhone',
          number: phoneToNumber(payment.billingAddress?.phone || selectedAddress.phone),
        },
      ],
    },
    shipTo: [
      {
        firstName: !isBopsOrder ? selectedAddress.firstName : clientFirstName,
        lastName: !isBopsOrder ? selectedAddress.lastName : clientLastName,
        phone: shippingPhones,
        address: !isBopsOrder ? selectedAddress : mapBopsAddress(bopsAddress),
        items: productsMapper(conciergeProducts, bopsAddress, shipmentMethod),
        ...(isParentheticalOrder && {
          deliveryPhoneNumber: formattedParentheticalPhone,
        }),
      },
    ],
    payments: _isEmpty(payment)
      ? []
      : [
          {
            cardType: convertToShortType(payment.networkType),
            keyIdentifier: payment.keyIdentifier,
            expiryMonth: Number(payment.cardExpMonth) || 0,
            expiryYear: Number(payment.cardExpYear) || 0,
            billingAddress: {
              ...payment.billingAddress,
              phone: [
                {
                  type: 'DayPhone',
                  number: phoneToNumber(payment.billingAddress.phone),
                },
              ],
            },
            amountReceived: getAmountReceived(shipmentMethod.orderTotalAmt, gcPayments),
          },
        ],
    gcPayments: getGCPayments(shipmentMethod.orderTotalAmt, gcPayments),
    alternateAssocPin,
    ...giftReceiptData,
    placeMaoOrder,
  };
};

export const mapDataForStoreInfo = (storeDetails, storeNum) => {
  const { name, addressDetails } = storeDetails;
  const { addressLine1, addressLine2, city, state, postalCode } = addressDetails?.[0];
  return {
    firstName: name,
    line1: addressLine1,
    line2: addressLine2,
    zip: postalCode,
    country: '',
    city,
    state,
    storeNum,
  };
};

export const mapBopsAvailability = (product) => {
  const { webProductSku, skuStores } = product;
  const skuId = webProductSku?.match(/\/(.*)/);
  let bopsAvailability = {};
  if (skuId) {
    bopsAvailability = skuStores
      ?.find((val) => val.skuId === skuId[1])
      ?.storeInventories?.reduce(
        (acc, curr) => (Number(curr.bopsQty) ? { ...acc, [Number(curr.storeNo)]: curr.bopsQty } : { ...acc }),
        {},
      );
  }

  return {
    ...product,
    bopsAvailability,
  };
};

export const mapBopsAvailabilityFarfetch = ({ farfetchConciergeProducts, skuInventory, skuStores }) => {
  const productsData = farfetchConciergeProducts.map((product) => {
    const { itemskuId } = product;
    let inventory = 0;
    let bopsAvailability = {};
    if (itemskuId) {
      bopsAvailability = skuStores
        ?.find((val) => val.pimSku === itemskuId)
        ?.storeInventories?.reduce(
          (acc, curr) => (Number(curr.bopsQty) ? { ...acc, [Number(curr.storeNo)]: curr.bopsQty } : { ...acc }),
          {},
        );
      inventory = skuInventory?.find((val) => val.pimSku === itemskuId)?.quantity;
    }

    return {
      ...product,
      inventory,
      bopsAvailability,
    };
  });

  return productsData;
};

/**
 * @param {string} orderId
 */
export const formatOrderId = (orderId = '') => {
  const formattedOrderId = orderId.replace(/:/g, '/');
  const startIndexOfDate = formattedOrderId.lastIndexOf('/') + 1;
  const orderDate = orderId.substring(startIndexOfDate, orderId.length);

  if (orderDate.length !== 8) {
    return orderId;
  }

  const formattedDate = orderDate.slice(4) + orderDate.slice(0, 4);
  return formattedOrderId.replace(orderDate, formattedDate);
};

const getAllLineItemsFromOrder = (order) => order.orderDetails.map((order) => order.items.map((lineItem) => lineItem));

/**
 *
 * @param {[]} lineItems
 * @param selectItems
 */
const reformatSelectedGiftItems = (lineItems, selectItems) => {
  const giftData = {};
  const allSelectedSkus = selectItems.map((skuId) => skuId.split('_')[0]);

  /**
   * Using the allSelectedSkus (e.g. ['401085749066', '401085749066']) to format any duplicates in an object,
   * so we have the proper quantities for the correct skus: {'401085749066': 2}
   */
  lineItems.forEach((lineItem) => {
    allSelectedSkus.forEach((item) => {
      if (lineItem.pimSkuId === item) {
        if (!giftData[lineItem.pimSkuId]) {
          giftData[lineItem.pimSkuId] = 1;
        } else {
          giftData[lineItem.pimSkuId]++;
        }
      }
    });
  });

  return giftData;
};

/**
 *
 * @param {{}} order
 * @param {[]} selectedItems
 */
const reformatLineItems = (order, selectedItems) => {
  const allLineItemsFromOrder = getAllLineItemsFromOrder(order)[0];
  const reformattedGiftData = reformatSelectedGiftItems(allLineItemsFromOrder, selectedItems);
  const lineItems = [];
  /**
   * Reformat the selected line items for the gift receipt.
   */
  allLineItemsFromOrder.forEach((lineItem) => {
    if (reformattedGiftData[lineItem.pimSkuId]) {
      lineItems.push(
        Object.assign(lineItem, {
          quantity: reformattedGiftData[lineItem.pimSkuId].toString(),
          price: {
            orderPrice: (parseFloat(lineItem?.price?.orderPrice) * reformattedGiftData[lineItem.pimSkuId])
              .toFixed(2)
              .toString(),
          },
        }),
      );
    }
  });

  return lineItems;
};

/**
 *
 * @param {{}} order
 * @param {[]} selectedItems
 */
export const formatOrderForGiftReceipt = (order, selectedItems) => {
  const defaultAmount = '0.00';
  const { firstName, lastName, email } = order.soldTo;
  const lineItems = reformatLineItems(order, selectedItems);

  return {
    recipient: {
      firstName: toTitleCase(firstName),
      lastName: toTitleCase(lastName),
      email,
    },
    orderData: {
      orderId: order.orderId,
      orderDate: order.orderDate,
      orderStatus: order.orderStatus,
      brandLine: order.brandLine,
      totalOrderAmount: order.totalOrderAmount || defaultAmount,
      totalSaleAmount: order.totalSaleAmount || defaultAmount,
      totalRefundAmount: order.totalRefundAmount || defaultAmount,
      subtotal: order.subtotal || defaultAmount,
      totalStateTaxAmount: order.totalStateTaxAmount || defaultAmount,
      totalCountyTaxAmount: order.totalCountyTaxAmount || defaultAmount,
      totalCityTaxAmount: order.totalCityTaxAmount || defaultAmount,
      totalLocalTaxAmount: order.totalLocalTaxAmount || defaultAmount,
      soldTo: {
        ...order.soldTo,
      },
      paymentDetails: {
        ...order.paymentDetails,
      },
      orderDetails: order.orderDetails.map((orders) => ({
        ...orders,
        items: lineItems.map((item) => ({
          ...item,
          thumbnailUrl: item?.thumbnailUrl || '', // BE requires us to send this property even if no image exists
          price: item?.price?.orderPrice || defaultAmount,
        })),
      })),
      assocRingingPin: order.assocRingingPin || order.associatePin,
    },
  };
};
